import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Download, Link2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail, downloadResumeFile } from "@/lib/api";
import { APPLICATION_STATUSES, SELECT_CONTENT_LIGHT, fmtDate } from "@/lib/constants";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdminWorkspace } from "@/components/admin/AdminWorkspace";
import { JobForm, StatusBadge } from "@/components/admin/JobForm";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const tabTriggerCls = "text-slate-500 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm";

const CandidateView = ({ applications }) => (
  <div className="mt-12" data-testid="candidate-applications">
    <h2 className="font-display text-2xl font-semibold text-slate-900">My applications</h2>
    {applications.length === 0 ? (
      <p className="mt-6 rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-400" data-testid="candidate-no-apps">
        No applications yet. Head to Careers and find your next chapter.
      </p>
    ) : (
      <div className="mt-6 space-y-4">
        {applications.map((a) => (
          <div key={a.id} className="rounded-2xl border border-slate-200 bg-white p-6 flex flex-wrap items-center justify-between gap-4" data-testid={`candidate-app-${a.id}`}>
            <div>
              <p className="font-display text-lg font-semibold text-slate-900">{a.job_title}</p>
              <p className="text-sm text-slate-500">{a.job_company} · applied {fmtDate(a.created_at)}</p>
            </div>
            <StatusBadge status={a.status} light />
          </div>
        ))}
      </div>
    )}
  </div>
);

const EmployerWorkspace = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, a, j] = await Promise.all([
        api.get("/stats"),
        api.get("/applications"),
        api.get("/jobs?mine=1"),
      ]);
      setStats(s.data);
      setApplications(a.data);
      setJobs(j.data);
    } catch {
      toast.error("Could not load dashboard data.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const deleteJob = async (job) => {
    if (!window.confirm(`Delete "${job.title}"? Applications for it will also be removed.`)) return;
    try {
      await api.delete(`/jobs/${job.id}`);
      toast.success("Role deleted.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const updateStatus = async (appId, status) => {
    try {
      await api.patch(`/applications/${appId}`, { status });
      setApplications((apps) => apps.map((a) => (a.id === appId ? { ...a, status } : a)));
      toast.success(`Marked as ${status}.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const statCards = stats
    ? [["My open roles", stats.open_jobs], ["My total roles", stats.jobs], ["Applications received", stats.applications]]
    : [];

  return (
    <div data-testid="employer-workspace">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-brand">Employer portal</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tighter text-slate-900" data-testid="dashboard-greeting">
            Hello, {user.name}.
          </h1>
        </div>
        {!showForm && (
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            data-testid="post-job-btn"
            className="flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-medium text-white transition-all duration-300 hover:bg-[#E63E12] hover:shadow-glow"
          >
            <Plus size={16} /> Post a role
          </button>
        )}
      </div>

      <div className="mt-10 rounded-[28px] border border-slate-200/80 bg-[#F5F6F8] p-4 sm:p-6 lg:p-8">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="dashboard-stats">
          {statCards.map(([label, value]) => (
            <div key={label} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`stat-card-${label.replace(/\s+/g, "-").toLowerCase()}`}>
              <p className="font-display text-3xl font-semibold text-slate-900">{value}</p>
              <p className="mt-1 text-[11px] uppercase tracking-[0.15em] text-slate-400">{label}</p>
            </div>
          ))}
        </div>

        {showForm && (
          <div className="mt-8">
            <JobForm
              light
              initial={editing}
              onSaved={() => { setShowForm(false); setEditing(null); load(); }}
              onCancel={() => { setShowForm(false); setEditing(null); }}
            />
          </div>
        )}

        <Tabs defaultValue="applications" className="mt-8">
          <TabsList className="bg-slate-200/70" data-testid="dashboard-tabs">
            <TabsTrigger value="applications" className={tabTriggerCls} data-testid="tab-applications">Applications ({applications.length})</TabsTrigger>
            <TabsTrigger value="jobs" className={tabTriggerCls} data-testid="tab-jobs">My roles ({jobs.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="applications" className="mt-8" data-testid="applications-panel">
            {applications.length === 0 ? (
              <p className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-400" data-testid="no-applications">
                No applications yet.
              </p>
            ) : (
              <div className="space-y-4">
                {applications.map((a) => (
                  <div key={a.id} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`application-row-${a.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <p className="font-display text-lg font-semibold text-slate-900">{a.name}</p>
                        <p className="text-sm text-slate-500">
                          for <span className="text-slate-700">{a.job_title}</span> · {a.job_company}
                        </p>
                        <p className="mt-2 text-xs text-slate-400">
                          {a.email} {a.phone ? `· ${a.phone}` : ""} · {fmtDate(a.created_at)}
                        </p>
                        {a.resume_file_id ? (
                          <button
                            onClick={() => downloadResumeFile(a.resume_file_id, a.resume_filename)}
                            data-testid={`resume-download-${a.id}`}
                            className="mt-2 flex items-center gap-1.5 text-xs text-brand hover:underline"
                          >
                            <Download size={12} /> Download resume{a.resume_filename ? ` (${a.resume_filename})` : ""}
                          </button>
                        ) : a.resume_link ? (
                          <a href={a.resume_link} target="_blank" rel="noopener noreferrer" data-testid={`resume-link-${a.id}`}
                            className="mt-2 inline-flex items-center gap-1.5 text-xs text-brand hover:underline">
                            <Link2 size={12} /> View resume link
                          </a>
                        ) : null}
                        {a.cover_note && <p className="mt-3 max-w-xl text-sm text-slate-500 italic">&ldquo;{a.cover_note}&rdquo;</p>}
                      </div>
                      <div className="flex items-center gap-3">
                        <StatusBadge status={a.status} light />
                        <Select value={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
                          <SelectTrigger data-testid={`status-select-${a.id}`} className="h-9 w-36 bg-white border-slate-200 text-slate-600 text-xs focus:ring-brand">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className={SELECT_CONTENT_LIGHT}>
                            {APPLICATION_STATUSES.map((s) => (
                              <SelectItem key={s} value={s} data-testid={`status-option-${a.id}-${s}`}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="jobs" className="mt-8" data-testid="jobs-panel">
            {jobs.length === 0 ? (
              <p className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-400" data-testid="no-jobs">
                No roles posted yet.
              </p>
            ) : (
              <div className="space-y-4">
                {jobs.map((j) => (
                  <div key={j.id} className="rounded-2xl border border-slate-200 bg-white p-6 flex flex-wrap items-center justify-between gap-4" data-testid={`job-row-${j.id}`}>
                    <div>
                      <div className="flex items-center gap-3">
                        <p className="font-display text-lg font-semibold text-slate-900">{j.title}</p>
                        <StatusBadge status={j.status} light />
                      </div>
                      <p className="mt-1 text-sm text-slate-500">{j.company} · {j.location} · {j.type}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => { setEditing(j); setShowForm(true); window.scrollTo(0, 0); }}
                        data-testid={`edit-job-${j.id}`}
                        className="flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs text-slate-600 hover:border-brand/60 hover:text-brand transition-colors duration-200"
                      >
                        <Pencil size={13} /> Edit
                      </button>
                      <button
                        onClick={() => deleteJob(j)}
                        data-testid={`delete-job-${j.id}`}
                        className="flex items-center gap-2 rounded-full border border-rose-200 px-4 py-2 text-xs text-rose-500 hover:border-rose-400 transition-colors duration-200"
                      >
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);

  useEffect(() => {
    if (user?.role === "candidate") {
      api.get("/applications").then((r) => setApplications(r.data)).catch(() => {});
    }
  }, [user]);

  if (!user) return null;

  return (
    <main data-testid="dashboard-page" className="pt-32 pb-32">
      <div className="max-w-7xl mx-auto px-6">
        {user.role === "admin" && <AdminWorkspace user={user} />}
        {user.role === "employer" && <EmployerWorkspace user={user} />}
        {user.role === "candidate" && (
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-brand">Candidate portal</p>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tighter text-slate-900" data-testid="dashboard-greeting">
              Hello, {user.name}.
            </h1>
            <CandidateView applications={applications} />
          </div>
        )}
      </div>
    </main>
  );
}
